<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import type { JournalAttribute } from '../types/journal.types'
import { useJournalFilterStore } from '../store/journalFilterStore'
import { useDateFilter } from '../composables/useDateFilter'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'

const props = defineProps<{
  attr: JournalAttribute
  journalId: number
  modelValue: unknown
  active: boolean
}>()

const emit = defineEmits<{ (e: 'update:modelValue', v: unknown): void; (e: 'apply'): void }>()
const CHOICE_TYPES = [] as string[]
const MULTI_TYPES = ['option', 'choice', 'multi_option', 'multiple_choice', 'content_object']

const isOpen = ref(false)
const localSimple = ref<string | number | boolean | null>(null)
const localChoice = ref<string[]>([])
const journalFilterStore = useJournalFilterStore()

const filterType = computed(() => {
  const t = props.attr.attribute_type
  if (t === 'string') {
    return 'text'
  }
  if (t === 'choice') {
    return 'option'
  }
  if (t === 'multiple_choice') {
    return 'multi_option'
  }

  return t
})

const isChoice = computed(() => CHOICE_TYPES.includes(filterType.value))
const isMulti = computed(() => MULTI_TYPES.includes(filterType.value))
const isDate = computed(() => ['date', 'date_time'].includes(filterType.value))
const needsChoices = computed(() => isChoice.value || isMulti.value)

const filterIcon = computed(() => (props.active ? 'ri-filter-fill' : 'ri-filter-2-line'))

const dateFilter = useDateFilter(filterType.value)
const { localFrom, localTo, setDatePreset } = dateFilter
const filterKey = computed(() => String(props.attr.id))

const choiceModelValue = computed((): string[] => {
  return localChoice.value
})

function onChoiceUpdate(val: string[]) {
  localChoice.value = val
}

function initFromModel() {
  const v = props.modelValue
  if (isDate.value) {
    dateFilter.initFromModel(v)
  } else if (needsChoices.value) {
    if (Array.isArray(v)) {
      localChoice.value = v.map(String)
    } else if (v != null && v !== '') {
      localChoice.value = [String(v)]
    } else {
      localChoice.value = []
    }
  } else {
    localSimple.value = v as string | number | boolean | null
  }
}

watch(isOpen, open => {
  if (open) {
    initFromModel()
    if (needsChoices.value) {
      journalFilterStore.fetchFilterChoices('journal', filterKey.value)
    }
  }
})

function apply() {
  let v: unknown = null
  if (isDate.value) {
    v = dateFilter.getValue()
  } else if (needsChoices.value) {
    v = isMulti.value
      ? (localChoice.value?.length ? localChoice.value : null)
      : (localChoice.value?.length ? localChoice.value[0] : null)
  } else {
    const s = localSimple.value

    v = (s !== null && s !== undefined && s !== '') ? s : null
  }
  emit('update:modelValue', v)
  emit('apply')
  isOpen.value = false
}

function onApplyChoice() {
  const v = isMulti.value
    ? (localChoice.value?.length ? localChoice.value : null)
    : (localChoice.value?.length ? localChoice.value[0] : null)

  emit('update:modelValue', v)
  emit('apply')
  isOpen.value = false
}

const boolItems = [
  { value: null, title: 'Все' },
  { value: true, title: 'Да' },
  { value: false, title: 'Нет' },
]

const fileItems = [
  { value: null, title: 'Все' },
  { value: true, title: 'Добавлен' },
  { value: false, title: 'Не добавлен' },
]

const datePresets = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'current_week', label: 'Текущая неделя' },
  { id: 'last_week', label: 'Прошлая неделя' },
  { id: 'current_month', label: 'Текущий месяц' },
  { id: 'last_month', label: 'Прошлый месяц' },
]
</script>

<template>
  <VMenu
    v-model="isOpen"
    :close-on-content-click="false"
    location="bottom"
    transition="scale-transition"
    :max-width="isDate ? 220 : 320"
  >
    <template #activator="{ props: menuProps }">
      <VIconBtn
        v-bind="menuProps"
        variant="plain"
        size="small"
        :icon="filterIcon"
        :color="active ? 'primary' : undefined"
        class="ml-1"
      />
    </template>
    <UniversalFilterTree
      v-if="needsChoices"
      :field-key="filterKey"
      module="journal"
      :store="journalFilterStore"
      :model-value="choiceModelValue"
      :placeholder="`Поиск: ${attr.short_name || attr.attribute_name}`"
      @update:model-value="onChoiceUpdate"
      @apply="onApplyChoice"
    />
    <VCard
      v-else
      class="pa-3"
    >
      <template v-if="filterType === 'text'">
        <VTextField
          v-model="localSimple"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :placeholder="`Поиск: ${attr.short_name || attr.attribute_name}...`"
          class="mb-2"
        />
      </template>
      <template v-else-if="filterType === 'integer'">
        <VTextField
          v-model="localSimple"
          type="number"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="Число"
          class="mb-2"
        />
      </template>
      <template v-else-if="filterType === 'float'">
        <VTextField
          v-model="localSimple"
          type="number"
          step="any"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="Число"
          class="mb-2"
        />
      </template>
      <template v-else-if="filterType === 'boolean'">
        <VSelect
          v-model="localSimple"
          :items="boolItems"
          item-value="value"
          item-title="title"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-2"
        />
      </template>
      <template v-else-if="filterType === 'date'">
        <VTextField
          v-model="localFrom"
          type="date"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="С"
          class="mb-2"
        />
        <VTextField
          v-model="localTo"
          type="date"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="По"
          class="mb-2"
        />
        <div class="d-flex flex-wrap gap-2 mb-2">
          <VChip
            v-for="preset in datePresets"
            :key="preset.id"
            size="small"
            class="preset-chip"
            @click="setDatePreset(preset.id)"
          >
            {{ preset.label }}
          </VChip>
        </div>
      </template>
      <template v-else-if="filterType === 'time'">
        <VTextField
          v-model="localSimple"
          type="time"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-2"
        />
      </template>
      <template v-else-if="filterType === 'date_time'">
        <VTextField
          v-model="localFrom"
          type="datetime-local"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="С"
          class="mb-2"
        />
        <VTextField
          v-model="localTo"
          type="datetime-local"
          variant="outlined"
          density="compact"
          hide-details
          placeholder="По"
          class="mb-2"
        />
        <div class="d-flex flex-wrap gap-2 mb-2">
          <VChip
            v-for="preset in datePresets"
            :key="preset.id"
            size="small"
            class="preset-chip"
            @click="setDatePreset(preset.id)"
          >
            {{ preset.label }}
          </VChip>
        </div>
      </template>
      <template v-else-if="filterType === 'file'">
        <VSelect
          v-model="localSimple"
          :items="fileItems"
          item-value="value"
          item-title="title"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-2"
        />
      </template>
      <template v-else>
        <VTextField
          v-model="localSimple"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          placeholder="Введите значение..."
          class="mb-2"
        />
      </template>
      <VBtn
        size="small"
        color="primary"
        block
        @click="apply"
      >
        Применить
      </VBtn>
    </VCard>
  </VMenu>
</template>
